import { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';

// Spec 2026-10-05 §15/4 — dönem detayı (GET /v1/evaluation-periods/{id}).
export function useEvaluationPeriodDetail(periodId) {
  const [period, setPeriod] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!periodId) return;
    api.get(`/v1/evaluation-periods/${periodId}`).then((res) => {
      if (res.data?.status) setPeriod(res.data.data);
    });
  }, [periodId, refreshTrigger]);

  return { period, refresh };
}

// Spec 2026-10-05 §8.3 — dönem katılımcısı uçları. Hata toast'ı interceptor'dadır; burada
// yalnız true/false döner (GenericFormDrawer false'ta açık kalır).
export function usePeriodParticipantsApi(periodId, queryParams) {
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 20 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!periodId) return;
    const params = { pageNumber: 1, pageSize: 20, ...(queryParams || {}), 'periodId[eq]': periodId };
    setFetching(true);
    api.get(`/v1/period-participants?${new URLSearchParams(params)}`)
      .then((res) => {
        const result = res.data?.data;
        setData(result?.data || []);
        setPagination({
          totalRowCount: result?.totalRowCount || 0,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || 20,
        });
      })
      .finally(() => setFetching(false));
  }, [periodId, queryParams, refreshTrigger]);

  const add = useCallback(async (employeeId, note) => {
    try { await api.post('/v1/period-participants', { periodId, employeeId, note }); refresh(); return true; } catch { return false; }
  }, [periodId, refresh]);

  const exclude = useCallback(async (id, reasonCode, note) => {
    try { await api.patch(`/v1/period-participants/${id}/exclusion`, { reasonCode, note }); refresh(); return true; } catch { return false; }
  }, [refresh]);

  const reinclude = useCallback(async (id) => {
    try { await api.delete(`/v1/period-participants/${id}/exclusion`); refresh(); return true; } catch { return false; }
  }, [refresh]);

  return { fetching, data, pagination, refresh, add, exclude, reinclude };
}

// Spec 2026-10-05 §15/2 — kapsam farkı listesi + ekle/çıkar mutasyonları (liste çekmeden).
export function usePeriodScopeDiffApi(periodId, enabled = true) {
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!periodId || !enabled) { setData([]); return; }
    setFetching(true);
    api.get(`/v1/period-participants/scope-diff?${new URLSearchParams({ 'periodId[eq]': periodId })}`)
      .then((res) => setData(res.data?.status ? (res.data.data || []) : []))
      .finally(() => setFetching(false));
  }, [periodId, enabled, refreshTrigger]);

  const add = useCallback(async (employeeId, note) => {
    try { await api.post('/v1/period-participants', { periodId, employeeId, note }); refresh(); return true; } catch { return false; }
  }, [periodId, refresh]);

  const exclude = useCallback(async (participantId, reasonCode, note) => {
    try { await api.patch(`/v1/period-participants/${participantId}/exclusion`, { reasonCode, note }); refresh(); return true; } catch { return false; }
  }, [refresh]);

  return { fetching, data, refresh, add, exclude };
}

// Analitik Plan 2 (spec §4.4, §9/3) — dönemin seviye bantları (yalnız İK). Değişiklik gerekçe ister;
// kaydedilince projektör dönemi yeniden hesaplar.
export function usePeriodBandsApi(periodId) {
  const [fetching, setFetching] = useState(false);
  const [bands, setBands] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!periodId) return;
    setFetching(true);
    api.get(`/v1/evaluation-periods/${periodId}/bands`)
      .then((res) => setBands(res.data?.status ? res.data.data : null))
      .finally(() => setFetching(false));
  }, [periodId, refreshTrigger]);

  const save = useCallback(async (payload) => {
    try { await api.put(`/v1/evaluation-periods/${periodId}/bands`, payload); refresh(); return true; } catch { return false; }
  }, [periodId, refresh]);

  return { fetching, bands, save, refresh };
}

// Spec 2026-10-09 §5 — GET /v1/evaluation-periods/{id}/extensions (en yeni önce). api.js GET'te
// reddetmez; sinyal res.data.status.
export function usePeriodExtensions(periodId) {
  const [extensions, setExtensions] = useState([]);
  const [fetching, setFetching] = useState(false);
  useEffect(() => {
    if (!periodId) return;
    setFetching(true);
    api.get(`/v1/evaluation-periods/${periodId}/extensions`)
      .then((res) => { if (res.data?.status) setExtensions(Array.isArray(res.data.data) ? res.data.data : []); })
      .finally(() => setFetching(false));
  }, [periodId]);
  return { extensions, fetching };
}
